import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { axiosClient } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';

export interface IntervalsImport {
  status: 'running' | 'done' | 'failed' | null;
  done: number;
  total: number;
}

/** ``GET /intervals/status`` (docs/INTERVALS_PLAN.md §11). */
export interface IntervalsStatus {
  available: boolean;
  connected: boolean;
  configured?: boolean;
  needs_reconnect?: boolean;
  athlete_id?: string | null;
  connected_at?: string | null;
  last_activity_at?: string | null;
  rides?: number;
  import?: IntervalsImport | null;
}

export const INTERVALS_STATUS_KEY = ['intervals', 'status'] as const;

/** Toast key for a failed connect: 403 not available, 503 not configured. */
export function connectErrorKey(error: unknown): string {
  const code = isAxiosError(error) ? error.response?.status : undefined;
  if (code === 403) return 'intervals.errors.notAvailable';
  if (code === 503) return 'intervals.errors.notConfigured';
  return 'intervals.errors.connectFailed';
}

/**
 * intervals.icu link for Profile → Integrations (API ``routes/intervals.py``).
 *
 * - ``connect()``: asks the API for intervals.icu's authorize URL and sends
 *   the browser there; intervals.icu returns to the API's callback page.
 * - ``disconnect(deleteRides)``: ends the link, optionally deleting the
 *   intervals.icu rides too.
 *
 * The status polls every 3 s while the first 90-day import runs.
 */
export function useIntervals() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addToast = useStore((s) => s.addToast);

  const status = useQuery<IntervalsStatus>({
    queryKey: INTERVALS_STATUS_KEY,
    queryFn: () => axiosClient.get<IntervalsStatus>('/intervals/status').then((r) => r.data),
    staleTime: 5_000,
    refetchInterval: (query) => (query.state.data?.import?.status === 'running' ? 3000 : false),
  });

  const connect = useMutation({
    mutationFn: () =>
      axiosClient
        .post<{ authorize_url: string }>('/intervals/connect', { client: 'web' })
        .then((r) => r.data),
    onSuccess: ({ authorize_url }) => {
      globalThis.location.href = authorize_url;
    },
    onError: (error) => addToast({ message: t(connectErrorKey(error)), type: 'error' }),
  });

  const disconnect = useMutation({
    mutationFn: (deleteRides: boolean) =>
      axiosClient
        .post<{ status: string; rides_deleted: number }>('/intervals/disconnect', {
          delete_rides: deleteRides,
        })
        .then((r) => r.data),
    onSuccess: (data) => {
      addToast({
        message:
          data.rides_deleted > 0
            ? t('intervals.toasts.ridesDeleted', { count: data.rides_deleted })
            : t('intervals.toasts.disconnected'),
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: INTERVALS_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: ['rides'] });
    },
    onError: () => addToast({ message: t('intervals.errors.disconnectFailed'), type: 'error' }),
  });

  return { status, connect, disconnect };
}
